import type { CityConfig } from "../city-template/types";

export const inclineVillageConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Incline Village",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "incline-village",
  route: "/cities/incline-village",
  title: "Emergency Passport Incline Village NV | Expedited Passport Renewal North Lake Tahoe | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport renewal in Incline Village, NV — as fast as 24 hours. Fast passport services for Incline Village, Crystal Bay, and North Lake Tahoe travelers. A+ BBB rated, registered State Dept. courier. No agency visit required.",
  h1: "Emergency & Expedited Passport Services in Incline Village, NV",
  heroSubtitle: "Passport expired before your Lake Tahoe trip abroad? Fast Passport Center helps Incline Village, Crystal Bay, and North Shore travelers get expedited passports in as little as 24 hours. No agency visit required — Nevada has no local passport agency.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Incline%20Village%20Nevada%20North%20Lake%20Tahoe%20shoreline%20with%20crystal%20clear%20turquoise%20water%20and%20dramatic%20Sierra%20Nevada%20mountain%20peaks%2C%20luxury%20lakefront%20homes%20nestled%20among%20towering%20pine%20forest%2C%20upscale%20mountain%20resort%20community%20atmosphere%2C%20clear%20alpine%20sky%20reflecting%20in%20the%20lake%20surface%2C%20pristine%20natural%20beauty%20with%20granite%20boulders%20along%20the%20shoreline%2C%20panoramic%20landscape%20photography%20showing%20the%20lake%20and%20surrounding%20peaks%2C%20peaceful%20serene%20morning%20light%20over%20the%20Tahoe%20basin%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=incline-village-nv-hero-001&orientation=landscape",
  heroImageAlt: "Incline Village Nevada North Lake Tahoe shoreline with Sierra Nevada mountains",
  geoPosition: "39.2518;-119.9520",
  geoPlacename: "Incline Village, Nevada",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St, Suite 600, San Francisco, CA 94105",
    distance: "Located in San Francisco — approximately 200 miles from Incline Village via I-80",
  },
  nearbyCitiesZips: [
    { name: "Incline Village", zips: "89451, 89450" },
    { name: "Crystal Bay", zips: "89402" },
    { name: "Kings Beach (CA)", zips: "96143" },
    { name: "Tahoe City (CA)", zips: "96145" },
    { name: "Truckee (CA)", zips: "96161, 96162" },
    { name: "Reno", zips: "89501, 89502, 89503" },
    { name: "Carson City", zips: "89701" },
    { name: "South Lake Tahoe (CA)", zips: "96150" },
    { name: "Stateline", zips: "89449" },
    { name: "Alpine Meadows (CA)", zips: "96146" },
    { name: "Squaw Valley (CA)", zips: "96146" },
    { name: "Verdi", zips: "89439" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Incline Village and Lake Tahoe departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — ship from any UPS in Incline Village or Tahoe City" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Reno", route: "/cities/reno" },
    { name: "Fast passport renewal in Carson City", route: "/cities/carson-city" },
    { name: "Expedited passport help in Sparks", route: "/cities/sparks" },
    { name: "Urgent passport services in Sacramento", route: "/cities/sacramento" },
    { name: "Same-day passport help in San Francisco", route: "/cities/san-francisco" },
    { name: "Passport courier services in Northern Nevada", route: "/states/nevada" },
  ],
  fpcOfficeAddress: "100 W Liberty St, Reno, NV 89501",
  fpcOfficeNote: "Fast Passport Center serves Incline Village remotely via secure overnight courier. We do not have a walk-in office in Incline Village — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for North Lake Tahoe residents.",
  fpcOfficeDistance: "Serving all of Incline Village, Crystal Bay, and the North Lake Tahoe shoreline",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Incline Village and the North Shore of Lake Tahoe. This exclusive mountain community — home to luxury ski resorts, second homeowners, and international travelers — sees heavy demand for fast passport services. Whether you're a full-time resident adapting to Tahoe living or a seasonal visitor needing an emergency passport, we deliver fast results.",
  nearbyCitiesIntro: "We serve all North Lake Tahoe communities including Incline Village, Crystal Bay, Kings Beach (CA), Tahoe City (CA), Truckee (CA), and the broader North Shore and Sierra region. Our overnight shipping labels work from any UPS location — mountain living and lake access don't affect our processing speed.",
  howItWorksIntro: "Most Incline Village travelers complete everything online and ship documents overnight. No need to navigate mountain roads to Reno, Sacramento, or San Francisco. Our emergency passport process delivers fast results for Lake Tahoe travelers.",
  mistakesIntro: "Incline Village residents enjoy mountain luxury living — but mountain roads and winter conditions complicate agency visits. Here are the most common passport mistakes we see.",
  testimonialsIntro: "Incline Village and North Lake Tahoe travelers trust us when their international departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Incline Village? Here are the most common ones we hear from North Lake Tahoe travelers.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  dmvWebsite: "https://dmv.nv.gov",
  dmvLabel: "Nevada DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Nearest agency is San Francisco (200 miles over Donner Pass)",
        "Winter road closures are a real risk",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Incline Village travelers with immediate departure needs — as fast as 24 hours for urgent flights from Reno-Tahoe International Airport.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for North Shore adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance for Incline Village and North Lake Tahoe residents.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance — especially helpful for travelers who lose passports at Lake Tahoe ski resorts, beaches, or casinos.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Incline Village Nevada travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens in Incline Village NV and North Lake Tahoe." },
    { name: "New Passport", description: "First-time passport application assistance for Incline Village and North Lake Tahoe residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "William C.",
      location: "Incline Village, NV",
      rating: 5,
      text: "Second homeowner here and realized my passport expired right before a trip to France. Fast Passport Center renewed it in 2 days. Incredible service in a mountain town. Made my flight with time to spare.",
      service: "Passport Renewal",
    },
    {
      name: "Karen S.",
      location: "Crystal Bay, NV",
      rating: 5,
      text: "Lost my passport at a Tahoe casino the night before a ski trip to the Swiss Alps. Called in a panic and they replaced it in 36 hours. I still can't believe they pulled it off. Ski trip saved!",
      service: "Lost Passport",
    },
    {
      name: "Robert M.",
      location: "Incline Village, NV",
      rating: 5,
      text: "Full-time Tahoe resident needing a fast renewal for a business trip to Japan. The thought of driving over Donner Pass in winter for the San Francisco agency was terrifying. Fast Passport Center handled everything from home. 48 hours, done.",
      service: "Emergency Passport",
    },
    {
      name: "Lisa K.",
      location: "Incline Village, NV",
      rating: 5,
      text: "My kids needed first passports for a family trip to Italy. We were worried about finding an acceptance facility up here and getting everything done in time. Fast Passport Center guided us through the whole thing and passports arrived in 5 days.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "inclinevillage-1",
      name: "Incline Village Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "930 Tahoe Blvd",
      city: "Incline Village",
      state: "NV",
      zip: "89451",
      phone: "(775) 831-8776",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Incline Village",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Incline Village facility. Limited hours — call ahead for passport acceptance. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "inclinevillage-2",
      name: "Kings Beach Post Office (CA)",
      type: "USPS Passport Acceptance Facility",
      address: "2037 Secline St",
      city: "Kings Beach",
      state: "CA",
      zip: "96143",
      phone: "(530) 546-3558",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3 miles west of Incline Village",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative facility just across the state line. Convenient for Crystal Bay and Incline Village residents.",
      icon: "ri-mail-line",
    },
    {
      id: "inclinevillage-3",
      name: "Truckee Post Office (CA)",
      type: "USPS Passport Acceptance Facility",
      address: "10051 Bridge St",
      city: "Truckee",
      state: "CA",
      zip: "96161",
      phone: "(530) 587-6010",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles from Incline Village",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Larger facility in Truckee. Full passport services. 15 miles from Incline Village via CA-267.",
      icon: "ri-mail-line",
    },
    {
      id: "inclinevillage-4",
      name: "Reno Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "50 S Virginia St",
      city: "Reno",
      state: "NV",
      zip: "89501",
      phone: "(775) 788-0900",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "35 miles from Incline Village",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest passport facility in Northern Nevada. 35-mile drive via Mount Rose Highway (NV-431). Full services.",
      icon: "ri-mail-line",
    },
    {
      id: "inclinevillage-5",
      name: "Washoe County Government Complex",
      type: "County Clerk Passport Acceptance",
      address: "1001 E 9th St",
      city: "Reno",
      state: "NV",
      zip: "89512",
      phone: "(775) 328-3275",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "35 miles from Incline Village",
      website: "https://www.washoecounty.gov/",
      websiteLabel: "Washoe County Clerk",
      notes: "Full-service county facility. Extended hours and passport acceptance. 35 miles via Mount Rose Highway.",
      icon: "ri-government-line",
    },
    {
      id: "inclinevillage-6",
      name: "Tahoe City Post Office (CA)",
      type: "USPS Passport Acceptance Facility",
      address: "950 N Lake Blvd",
      city: "Tahoe City",
      state: "CA",
      zip: "96145",
      phone: "(530) 583-2100",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles west of Incline Village",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for West Shore travelers. Serves Tahoe City to Incline Village corridor.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 200 miles to San Francisco over Donner Pass", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Lake Tahoe photo shops may not follow strict passport specs. Photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at small mountain acceptance facilities like the Incline Village Post Office.",
    },
    {
      icon: "ri-cloud-windy-line",
      title: "Ignoring Winter Road Conditions",
      description: "Incline Village's Mount Rose Highway and I-80 over Donner Pass can be closed or require chains in winter. The nearest passport agency in San Francisco is 200 miles away — and completely unreachable in heavy snow. Our courier service keeps you off mountain roads.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, you'll be turned away at any regional passport agency. Don't risk driving 200 mountain miles only to be rejected.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This is especially important for Tahoe families where parents may have different schedules or travel separately.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start in a Mountain Town",
      description: "Mail service to and from Incline Village can be affected by winter weather. Standard processing takes 8–11 weeks. Start expedited at least 3 weeks before travel to account for mountain shipping delays.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Incline Village, Tahoe City, or Truckee." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Incline Village address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Incline Village, NV?",
      a: "Fast Passport Center offers emergency passport processing in Incline Village in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities. Nevada has no local passport agency — the nearest is San Francisco, 200 miles away via I-80 over Donner Pass — which can be treacherous or closed in winter. Our courier service is the fastest and safest option for Lake Tahoe travelers.",
    },
    {
      q: "What is the nearest passport agency to Incline Village?",
      a: "The nearest passport agency is the San Francisco Passport Agency, approximately 200 miles from Incline Village. The route includes driving over Donner Pass on I-80 — which regularly experiences chain controls, closures, and dangerous conditions from November through April. Fast Passport Center's courier service eliminates any need for mountain travel entirely.",
    },
    {
      q: "Does Incline Village have its own passport acceptance facility?",
      a: "Yes. The Incline Village Post Office at 930 Tahoe Blvd offers passport acceptance services by appointment. However, hours are limited and the facility is small. Nearby alternatives include the Kings Beach Post Office (3 miles west in CA), Truckee Post Office (15 miles), and the full-service Reno Main Post Office (35 miles via Mount Rose Highway).",
    },
    {
      q: "Do you serve Crystal Bay and the California side of North Lake Tahoe as well?",
      a: "Yes. Fast Passport Center serves the entire North Shore of Lake Tahoe including Crystal Bay (NV), Kings Beach (CA), Tahoe City (CA), Alpine Meadows, Squaw Valley, and Truckee (CA). Our overnight shipping labels work from any UPS location across both states. Living right on the state line doesn't complicate anything for us.",
    },
    {
      q: "Can I get expedited passport processing during Tahoe winter storm season?",
      a: "Yes, and this is one of our biggest advantages for Incline Village residents. Winter storms can close Donner Pass and Mount Rose Highway for days at a time. Our courier service operates on a national network unaffected by local mountain weather, so you get 24–48 hour processing regardless of snow conditions outside your window.",
    },
    {
      q: "I'm a seasonal resident of Incline Village — can you help with a passport emergency?",
      a: "Absolutely. Incline Village has a significant population of second homeowners and seasonal residents. Whether you're here for ski season, summer at the lake, or just visiting, Fast Passport Center can process your passport and deliver it to your Incline Village address. You don't need to be a Nevada permanent resident.",
    },
    {
      q: "How much does expedited passport service cost in Incline Village?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your departure date.",
    },
  ],
  photoLocations: [
    { name: "Incline Village Post Office", address: "930 Tahoe Blvd, Incline Village, NV 89451", hours: "Mon–Fri: 9AM–4PM (photo by appointment)", website: "https://tools.usps.com/find-location.htm", websiteLabel: "USPS Location" },
    { name: "CVS Pharmacy — Incline Village", address: "913 Tahoe Blvd, Incline Village, NV 89451", hours: "Mon–Sun: 8AM–9PM", website: "https://www.cvs.com/store-locator/cvs-pharmacy-photo", websiteLabel: "CVS Photo" },
    { name: "UPS Store — Truckee", address: "11209 Brockway Rd, Truckee, CA 96161", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–4PM", website: "https://www.theupsstore.com/tools/find-a-store", websiteLabel: "UPS Store" },
    { name: "Safeway — Kings Beach", address: "1925 Lake Tahoe Blvd, Kings Beach, CA 96143", hours: "Mon–Sun: 6AM–11PM", website: "https://www.safeway.com/", websiteLabel: "Safeway" },
    { name: "FedEx Office — Reno", address: "1201 Terminal Way, Reno, NV 89502", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.fedex.com/en-us/location.html", websiteLabel: "FedEx Office" },
    { name: "Walgreens — Reno (Mt Rose)", address: "6150 Mae Anne Ave, Reno, NV 89523", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/storelocator", websiteLabel: "Walgreens Photo" },
    { name: "CVS Pharmacy — Truckee", address: "11399 Deerfield Dr, Truckee, CA 96161", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/store-locator/cvs-pharmacy-photo", websiteLabel: "CVS Photo" },
    { name: "Costco — Reno", address: "333 Bill Ave, Reno, NV 89502", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM", website: "https://www.costco.com/warehouse-locations", websiteLabel: "Costco Photo" },
  ],
  dmvLocations: [
    {
      name: "Nevada DMV — Reno",
      address: "555 Wright Way, Carson City, NV 89711",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Closest full-service Nevada DMV to Incline Village. 35 miles via Mount Rose Highway. REAL ID processing. Online appointment recommended.",
      featured: true,
    },
    {
      name: "Nevada DMV — Carson City",
      address: "555 Wright Way, Carson City, NV 89711",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "State capital DMV. Alternative to Reno. 30 miles via US-50 and NV-28 along the East Shore.",
      featured: true,
    },
    {
      name: "California DMV — Truckee",
      address: "11357 Donner Pass Rd, Truckee, CA 96161",
      hours: "Mon–Fri: 8AM–5PM",
      note: "California DMV 15 miles from Incline Village. For Tahoe residents who also need CA documentation.",
      featured: false,
    },
    {
      name: "Washoe County Clerk",
      address: "1001 E 9th St, Reno, NV 89512",
      hours: "Mon–Fri: 8AM–5PM",
      note: "County government with passport acceptance. 35 miles from Incline Village via Mount Rose Highway.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "RNO",
      name: "Reno-Tahoe International Airport",
      distance: "35 miles from Incline Village via Mount Rose Highway (NV-431)",
      address: "2001 E Plumb Ln, Reno, NV 89502",
      note: "Primary airport for Incline Village and Lake Tahoe. Direct service to Mexico and connecting international flights. 35-mile drive — plan extra time in winter for mountain road conditions.",
      flightUrl: "https://www.renoairport.com/",
      flightLabel: "Reno-Tahoe Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "200 miles from Incline Village via I-80",
      address: "San Francisco, CA 94128",
      note: "Primary West Coast hub. Home to the San Francisco Passport Agency. Requires crossing Donner Pass on I-80.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Airport",
      featured: false,
    },
    {
      code: "SMF",
      name: "Sacramento International Airport",
      distance: "130 miles from Incline Village via I-80",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "Alternative regional hub. Some Tahoe travelers prefer Sacramento for certain connecting international flights.",
      flightUrl: "https://www.sacramento.aero/smf/",
      flightLabel: "Sacramento Airport",
      featured: false,
    },
  ],
};